import { cloneElement, isValidElement, useId, useState } from 'react'

import { Alert } from '../Alert'
import { Button } from '../Button/Button'
import { Field } from '../Form/Field'
import { Input } from '../Form/Input'
import { Modal } from './Modal'
import { ModalBody } from './ModalBody'
import { ModalFooter } from './ModalFooter'
import { ModalHeader } from './ModalHeader'

export const ConfirmNameModal = ({
  actionLabel = 'Delete',
  actionPendingLabel,
  children,
  confirmationAction,
  confirmationButtonLabel,
  isPending = false,
  name = '',
  onConfirm,
  requireNameConfirmation = true,
  title,
  trigger,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState('')
  const inputId = useId()
  const isConfirmed = !requireNameConfirmation || confirmation === name

  const close = () => {
    if (isPending) {
      return
    }

    setIsOpen(false)
    setConfirmation('')
    setError('')
  }

  const submit = async event => {
    event.preventDefault()
    if (!isConfirmed || isPending) {
      return
    }

    setError('')
    try {
      await onConfirm?.()
      close()
    } catch (submitError) {
      setError(submitError?.message || `Unable to ${confirmationAction || actionLabel.toLowerCase()}. Please try again.`)
    }
  }

  return (
    <>
      {isValidElement(trigger) && cloneElement(trigger, { onClick: event => {
        trigger.props.onClick?.(event)
        if (event.defaultPrevented) return
        setError('')
        setIsOpen(true)
      } })}
      <Modal isOpen={isOpen} toggle={close}>
        <ModalHeader>{title}</ModalHeader>
        <form onSubmit={submit}>
          <ModalBody>
            {error && <Alert color="danger" className="tw:mb-4">{error}</Alert>}
            <p className="tw:text-lg tw:text-foreground">{children}</p>
            {requireNameConfirmation && (
              <Field
                className="tw:mt-4"
                helper={<>Type <strong className="tw:text-brand-red-600">{name}</strong> to {confirmationAction}.</>}
                label={`Confirm ${name}`}
              >
                <Input
                  autoComplete="off"
                  id={inputId}
                  onChange={event => setConfirmation(event.target.value)}
                  spellCheck="false"
                  value={confirmation}
                />
              </Field>
            )}
          </ModalBody>
          <ModalFooter>
            <span className="tw:flex-1" />
            <Button onClick={close} type="button" variant="secondary">Cancel</Button>
            <Button disabled={!isConfirmed || isPending} type="submit" variant="danger-solid">
              {isPending ? actionPendingLabel || `${actionLabel}…` : confirmationButtonLabel || actionLabel}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </>
  )
}
